import { Vue, Component, Prop } from 'vue-property-decorator'
import { MarkingAdInfo } from '~/types/api/MarkingAdTypes'
import Checkbox from './Checkbox'
import Product from '~/types/api/Product'
import ImageElement from '~/components/element/ImageElement'
import JsxHelper from '~/utils/JsxHelper'
import DiscountSvg from '~/assets/svg/discount.svg?inline'
import HotSvg from '~/assets/svg/hot.svg?inline'
import NewSvg from '~/assets/svg/new.svg?inline'
import List from '~/assets/svg/list.svg?inline'
import ProductDaySvg from '~/assets/svg/product-day.svg?inline'
import HeartSvg from '~/assets/svg/heart.svg?inline'
import SuccessSvg from '~/assets/svg/product-success.svg?inline'
import BasketNumber from '~/components/element/BasketNumber'
import SectionHelper from '~/utils/sectionHelper'
import RatingStars from '~/components/element/RatingStars'
import s from '~/utils/s'
import SwitchElement from '~/components/element/SwitchElement'
import { CartItem } from '~/types/api/SfCart'
import Img from '~/types/api/Img'
import { MetrikaLocalCodes } from '~/types/api/MetrikaCode'
import MarkingAd from '~/components/element/MarkingAd'
import UserLists from '~/components/page/userProductList/UserLists'
import UserProductListNumber from '~/components/element/UserProductListNumber'
import CloseSvg from '~/assets/svg/close-popup.svg?inline'
import { E_COMMERCE_OPERATIONS } from '~/types/enum/ECommerce'

@Component
export default class ProductCard extends Vue {
  isShowSuccess: boolean = false
  sussessTimeout: NodeJS.Timeout | undefined
  renderUserLists: boolean = false
  renderUserListsMobile: boolean = false

  @Prop({ type: Object, required: true })
  readonly product!: Product

  @Prop({ type: Number, default: 0 })
  readonly position?: number

  @Prop({ type: Boolean, default: false })
  readonly isCatalog?: boolean

  @Prop({ type: Boolean, default: false })
  readonly isUserList?: boolean

  beforeDestroy () {
    if (this.sussessTimeout) {
      clearTimeout(this.sussessTimeout)
    }
  }

  select (val: boolean) {
    if (val) {
      s(this.$store).userProductList.addSelectedId(this.product.id)
      s(this.$store).userProductList.reCalcResult()
    } else {
      s(this.$store).userProductList.removeSelectedId(this.product.id)
      s(this.$store).userProductList.reCalcResult()
    }
  }

  remove () {
    const userProductListId = s(this.$store).userProductList.items.userProductList.id
    const itemId = s(this.$store).userProductList.items.userProductList.items.find((item) => {
      if (item.product.id === this.product.id) { return item.id } else { return null }
    })
    s(this.$store).userProductList.removeFromUserProductList({ productId: itemId?.id!, userProductListId })
  }

  get isSelected (): boolean {
    return !!s(this.$store).userProductList.selectedIds.includes(this.product.id)
  }

  get title (): string {
    return this.product.title
  }

  get titleLines (): number {
    if (this.product.byPrescription || this.product.isVeterinary || this.isMnn) {
      return 2
    } else {
      return 6
    }
  }

  get isMnn (): boolean {
    return !!(this.product.mnn && this.product.mnn.length > 0)
  }

  get isDiscount (): boolean {
    return this.product.price < this.product.basePrice
  }

  get basketItem (): CartItem | undefined {
    return s(this.$store).basket.items.find(e => e.product.id === this.product.id)
  }

  get isBasket (): boolean {
    return this.basketItem !== undefined
  }

  get isWaitingList (): boolean {
    return s(this.$store).waitingList.items.map(id => id.product.id).includes(this.product.id)
  }

  get isWaitingListModifier (): any {
    if (this.isWaitingList) {
      return ['waiting_selected']
    }
    return []
  }

  get isAvailable (): boolean {
    return this.product.available > 0
  }

  get link (): string {
    const helper = new SectionHelper(s(this.$store).general.sections, this.$store)
    const sections = this.product.sectionId
    return helper.getProductLink(sections?.length ? sections[0] : s(this.$store).categoryPage.category.id, this.product.seoUrl ?? this.product.id)
  }

  get defaultImg (): Img {
    return {
      d: require('~/assets/image/pill.png'),
      t: require('~/assets/image/pill.png'),
      m: require('~/assets/image/pill.png')
    }
  }

  get creativeTokenId (): string {
    return this.product.creativeTokenId!
  }

  get markingAdInfo (): MarkingAdInfo {
    s(this.$store).markingAd.loadMarkingInfo(this.creativeTokenId)
    return {
      id: this.creativeTokenId,
      creativeToken: s(this.$store).markingAd.creativeToken,
      description: s(this.$store).markingAd.description,
      clientName: s(this.$store).markingAd.clientName,
      advertiserUrl: s(this.$store).markingAd.advertiserUrl
    }
  }

  async addToBasket () {
    this.isShowSuccess = await s(this.$store).basket.add(this.product.id)
    await s(this.$store).waitingList.removeProductFromWaitingList(this.product.id)
    this.sussessTimeout = setTimeout(() => { this.isShowSuccess = false }, 2000)
    if (this.isWaitingList) {
      await this.addToWaitingList(false)
    }
  }

  async addToWaitingList (value: boolean) {
    if (value && !this.isWaitingList) {
      await s(this.$store).waitingList.addProductToWaitingList(this.product.id)
    } else if (!value && this.isWaitingList) {
      await s(this.$store).waitingList.removeProductFromWaitingList(this.product.id)
    }
  }

  get isFavorite (): boolean {
    return s(this.$store).wishlist.ids.includes(this.product.id)
  }

  get inUList (): boolean {
    const userProductLists = s(this.$store).userProductList.userProductLists
    return userProductLists.some((upl: any) => {
      return upl.items.some((item: any) => item.productId === this.product.id)
    })
  }

  renderImage () {
    if (this.product.img) {
      return (
        <ImageElement {...{
          props: {
            img: Object.keys(this.product.img).length !== 0 ? this.product.img : this.defaultImg,
            isMicrodata: true,
            title: this.product.title,
            alt: this.product.title
          }
        }} />
      )
    }
  }

  renderMmn () {
    if (this.isMnn) {
      return (
        <div class="product-card__info-mnn" title={this.product.mnn}>
            MNN {this.product.mnn}
        </div>
      )
    }
  }

  renderDiscount () {
    if (this.isDiscount) {
      return (
        <div class="product-card__discount-price">
          {this.product.basePrice} ₽
        </div>
      )
    }
  }

  renderBasketButton () {
    if (this.isUserList) {
      return (
        <UserProductListNumber {...{
          props: {
            id: this.product.id
          }
        }} />
      )
    } else if (this.isBasket) {
      return (
        <BasketNumber {...{
          props: {
            id: this.product.id
          }
        }} />
      )
    } else {
      return (
        <div class="button-text" onClick={this.addToBasket}>
          В корзину
        </div>
      )
    }
  }

  renderWaitingListButton () {
    return (
      <SwitchElement {...{
        props: {
          value: this.isWaitingList,
          name: 'waiting',
          label: 'Сообщим о поступлении',
          modifiers: [...['waiting'], ...this.isWaitingListModifier]
        },
        on: {
          change: async (v: boolean) => {
            await this.addToWaitingList(v)
          }
        },
        scopedSlots: {
          textPayload: () => 'Сообщим о поступлении'
        }
      }} />
    )
  }

  renderSuccess () {
    if (this.isShowSuccess) {
      return (
        <div class="product-card__favorite-success">
          <div class="product-card__favorite-success-icon">
            <SuccessSvg />
          </div>
          <div class="product-card__favorite-success-text">
            В корзине
          </div>
        </div>
      )
    }
  }

  renderPriceWrapper () {
    if (this.isAvailable) {
      return (
        <div class="product-card__price-wrapper">
          <div class="product-card__price-container">
            {this.renderDiscount()}
            <div class={this.product.price.toString().length >= 5 ? 'product-card__greater-price' : 'product-card__price'} itemprop="offers" itemscope itemtype="https://schema.org/Offer">
              <span itemprop="price" content={this.product.price}>{this.product.price}</span>
              <span itemprop="priceCurrency" content="RUB"> ₽</span>
              <meta itemprop="availability" href="https://schema.org/InStock" content="InStock" />
            </div>
          </div>
          <div class="product-card__basket-wrapper">
            {this.renderBasketButton()}
          </div>
        </div>
      )
    } else {
      return (
        <div class="product-card__price-wrapper">
          <div class="product-card__basket-wrapper">
            <div class="product-card__basket-text-not-available">
              Товара нет в наличии
            </div>
            {this.renderWaitingListButton()}
          </div>
        </div>
      )
    }
  }

  renderRatingStars () {
    if (!this.product.rating) { return null }
    const { value: rating, reviews: { total: reviewsCount } } = this.product.rating

    return (
      <RatingStars {...{
        props: { rating, reviewsCount }
      }} />
    )
  }

  render () {
    const { canReview } = this.product

    const favorite = (
      <div class={{
        'product-card__favorite': true,
        'product-card__favorite_selected': this.isFavorite
      }} onClick={(event: MouseEvent) => {
        event.stopPropagation()
        s(this.$store).wishlist.changeProduct(this.product.id)
      }}>
        <HeartSvg />
      </div>
    )

    const toUserList = (
      <div {...{
        class: {
          'product-card__to-user-list': true,
          'product-card__to-user-list_selected': this.inUList
        },
        on: {
          click: (event: MouseEvent) => {
            event.preventDefault()
            this.renderUserLists = true
          }
        },
        directives: [{
          name: 'VueClickOutside',
          value: () => {
            this.renderUserLists = false
          }
        }]
      }}>
        <List />
        {this.renderUserLists &&
        <UserLists
          {...{
            props: {
              productId: this.product.id,
              product: this.product,
              route: this.$store.$router.currentRoute
            }
          }}/>}
      </div>
    )

    const toUserListMobile = (
      <div {...{
        class: {
          'product-card__to-user-list': true,
          'product-card__to-user-list_selected': this.inUList
        },
        on: {
          click: (event: MouseEvent) => {
            event.preventDefault()
            this.renderUserListsMobile = true
          }
        },
        directives: [{
          name: 'VueClickOutside',
          value: () => {
            this.renderUserListsMobile = false
          }
        }]
      }}>
        <List />
        {this.renderUserListsMobile &&
        <UserLists {...{
          props: {
            productId: this.product.id,
            product: this.product
          }
        }}/>}
      </div>
    )

    const discount = JsxHelper.if.call(
      this,
      this.product.promoAction,
      <div class="product-card__tags-item">
        <DiscountSvg />
      </div>
    )

    const productDay = JsxHelper.if.call(
      this,
      this.product.isProductDay,
      <div class="product-card__tags-item">
        <ProductDaySvg />
      </div>
    )

    const productHit = JsxHelper.if.call(
      this,
      this.product.isProductHit,
      <div class="product-card__tags-item">
        <HotSvg />
      </div>
    )

    const productNew = JsxHelper.if.call(
      this,
      this.product.isProductNew,
      <div class="product-card__tags-item">
        <NewSvg />
      </div>
    )

    const prescription = (isCatalog: boolean = false) => JsxHelper.if.call(
      this,
      this.product.byPrescription,
      <div class={{
        'product-card__info-prescription': true,
        'product-card__info-prescription_type_catalog': isCatalog
      }}>
        по рецепту
      </div>
    )

    const veterinary = (isCatalog: boolean = false) => JsxHelper.if.call(
      this,
      this.product.isVeterinary,
      <div class={{
        'product-card__info-prescription': true,
        'product-card__info-prescription_type_catalog': isCatalog
      }}>
        вет препарат
      </div>
    )

    const addViewECommerce = async () => {
      const regionId = s(this.$store).regions.regionId ?? 0
      const sectionId = s(this.$store).regions.sectionId ?? 0
      await s(this.$store).eCommerce.addToBasketECommerce({
        productId: s(this.$store).productPage.id,
        regionId,
        sectionId,
        type: E_COMMERCE_OPERATIONS.viewProduct
      })
    }

    return (
      <div class="product-card__list-element" itemscope itemtype="https://schema.org/ListItem" itemprop="itemListElement">
        <meta itemprop="position" content={this.position} />
        <div class={{
          'product-card': true,
          'product-card_type_catalog': this.isCatalog,
          'not-available': !this.isAvailable
        }} itemscope itemtype="https://schema.org/Product" itemprop="item" data-id={this.product.id}>
          <meta itemprop="name" content={this.title} />
          <meta itemprop="url" content={this.link} />
          <div class="product-card__tags-catlog-wrapper">
            {!this.isUserList && discount}
            {!this.isUserList && productHit}
            {!this.isUserList && productNew}
            {!this.isUserList && productDay}
            {prescription(true)}
            {!this.isUserList && veterinary(true)}
            <div class="product-card__marking-Ad">
              {this.creativeTokenId &&
              <MarkingAd {...{
                props: {
                  info: this.product.marking
                }
              }}/>
              }
            </div>
            {!this.isUserList && veterinary(true)}
            {this.isUserList &&
            <div class="product-basket__close user-list">
              <CloseSvg onClick={this.remove} />
            </div>
            }
            {favorite}
            {toUserListMobile}
          </div>
          <div class="product-card__image-wrapper" onclick={ addViewECommerce } >
            <nuxt-link to={this.link}>
              {this.renderImage()}
            </nuxt-link>
          </div>
          <div class="product-card__info-wrapper">
            <div class="product-card__info-title" onclick={ addViewECommerce }>
              <nuxt-link to={this.link} class="product-card__info-title" title={this.title}>
                  {this.title}
              </nuxt-link>
            </div>
            {this.renderMmn()}
            <div class="product-card__additional-info">
              {veterinary(false)}
              <div>
                {prescription(false)}
              </div>
              <div class="product-card__marking-Ad">
                {this.creativeTokenId &&
              <MarkingAd {...{
                props: {
                  info: this.product.marking
                }
              }}/>
                }
              </div>
            </div>
            <div class="product-card__rating">
              { canReview && this.renderRatingStars() }
            </div>
          </div>
          {this.renderPriceWrapper()}
          <div class="product-card__tags-wrapper">
            {this.isUserList &&
            <Checkbox {...{
              props: {
                value: this.isSelected,
                name: 'product_' + this.product.id,
                label: 'product_' + this.product.id,
                rules: {},
                isUserList: true
              },
              on: {
                change: this.select
              }
            }}/>}
            {!this.isUserList && discount}
            {!this.isUserList && productHit}
            {!this.isUserList && productNew}
            {!this.isUserList && productDay}
          </div>
          <div class="product-card__favorite-wrapper">
            {this.isUserList &&
            <div class="product-basket__close user-list">
              <CloseSvg onClick={this.remove} />
            </div>
            }
            {favorite}
            {toUserList}
          </div>
          <transition name="product-card__animate">
            {this.renderSuccess()}
          </transition>
        </div>
      </div>
    )
  }
}
